import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BannerPopup } from "./BannerPopup";
import { UPLOAD_API_BASE, CARTO_LIGHT_TILE_URL } from "./config/api";
import "./mapMarkers.css";

const selectedIcon = L.divIcon({
  className: 'om-marker-icon om-marker-selected',
  html: `<div class="om-marker-hit om-marker-hit--selected">
    <div class="om-marker-core om-marker-core--selected">
      <svg width="14" height="12" viewBox="0 0 14 12" fill="none"><rect x="1" y="1" width="12" height="8" rx="1.2" fill="#fff"/><path d="M4 11h6" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/></svg>
    </div>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

const dotIcon = L.divIcon({
  className: 'om-marker-icon om-marker-dot',
  html: `<div class="om-marker-hit om-marker-hit--dot">
    <div class="om-marker-core om-marker-core--dot"></div>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -10],
});

/** In-memory cache so toggling banner/traffic map does not re-download ~30MB JSON. */
const trafficMapDataCache = new Map();
const trafficMapInflight = new Map();
const TRAFFIC_MAP_CACHE_LIMIT = 2;

function rememberTrafficMapData(path, data) {
  trafficMapDataCache.set(path, data);
  while (trafficMapDataCache.size > TRAFFIC_MAP_CACHE_LIMIT) {
    const oldest = trafficMapDataCache.keys().next().value;
    trafficMapDataCache.delete(oldest);
  }
}

async function loadTrafficMapData(path) {
  const key = String(path || '');
  if (!key) throw new Error('empty traffic map path');
  if (trafficMapDataCache.has(key)) {
    return { status: 'ready', data: trafficMapDataCache.get(key) };
  }
  if (trafficMapInflight.has(key)) {
    return trafficMapInflight.get(key);
  }

  const request = (async () => {
    const mapUrl = `${UPLOAD_API_BASE}/api/traffic-map-data?path=${encodeURIComponent(key)}`;
    const response = await fetch(mapUrl);
    if (!response.ok) {
      if (response.status === 404) {
        return { status: 'pending', data: null };
      }
      throw new Error(`HTTP ${response.status}`);
    }
    const jsonData = await response.json();
    rememberTrafficMapData(key, jsonData);
    return { status: 'ready', data: jsonData };
  })().finally(() => {
    trafficMapInflight.delete(key);
  });

  trafficMapInflight.set(key, request);
  return request;
}

function normalizeMapCenter(center) {
  if (!center) return null;
  if (Array.isArray(center) && center.length === 2 && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
    return center;
  }
  if (center.lat != null && center.lng != null) {
    return [center.lat, center.lng];
  }
  return null;
}

function MapBridge({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    if (!mapRef) return undefined;
    mapRef.current = map;
    return () => {
      mapRef.current = null;
    };
  }, [map, mapRef]);
  return null;
}

function InvalidateSizeOnShow({ active, center, zoom }) {
  const map = useMap();
  const wasActive = useRef(false);
  useEffect(() => {
    if (!active) {
      wasActive.current = false;
      return undefined;
    }
    const justShown = !wasActive.current;
    wasActive.current = true;
    const t = window.setTimeout(() => {
      try {
        map.invalidateSize();
        if (!justShown) return;
        const latlng = normalizeMapCenter(center);
        if (latlng && Number.isFinite(zoom)) {
          map.setView(latlng, zoom, { animate: false });
        } else if (Number.isFinite(zoom)) {
          map.setZoom(zoom, { animate: false });
        }
      } catch {
        /* map may be disposed */
      }
    }, 40);
    return () => window.clearTimeout(t);
  }, [active, center, zoom, map]);
  return null;
}

export default function TrafficMap({
  bnr_zoom_coordinates,
  zoomLevel,
  choosed_banner_id,
  trafficMapPath,
  isVisible = true,
  mapRef = null,
}) {
  const cached = trafficMapPath ? trafficMapDataCache.get(String(trafficMapPath)) : null;
  const [selectedBannerId, setSelectedBannerId] = useState(choosed_banner_id || '');
  const [data, setData] = useState(cached || undefined);
  const [mapLoadStatus, setMapLoadStatus] = useState(
    !trafficMapPath ? 'no_path' : cached ? 'ready' : 'idle',
  );
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  useEffect(() => {
    setSelectedBannerId(choosed_banner_id || '');
  }, [choosed_banner_id]);

  const fetchData = async () => {
    if (!trafficMapPath) {
      setData(null);
      setMapLoadStatus('no_path');
      return;
    }
    const key = String(trafficMapPath);
    const hit = trafficMapDataCache.get(key);
    if (hit) {
      setData(hit);
      setMapLoadStatus('ready');
      if (choosed_banner_id) setSelectedBannerId(choosed_banner_id);
      return;
    }

    setMapLoadStatus('loading');
    try {
      const result = await loadTrafficMapData(key);
      if (result.status === 'pending') {
        setMapLoadStatus('pending');
        setData(null);
        return;
      }
      setData(result.data);
      setMapLoadStatus('ready');
      if (choosed_banner_id) setSelectedBannerId(choosed_banner_id);
    } catch (e) {
      console.error('Loading error:', e);
      setMapLoadStatus('error');
    }
  };

  useEffect(() => {
    fetchData();
  }, [trafficMapPath]);

  useEffect(() => {
    if ((mapLoadStatus !== 'pending' && mapLoadStatus !== 'error') || !trafficMapPath) {
      return undefined;
    }
    const timer = setTimeout(() => {
      fetchData();
    }, 5000);
    return () => clearTimeout(timer);
  }, [mapLoadStatus, trafficMapPath]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsMapFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const trafficNorm = (count, minCount, maxCount) => {
    if (!(maxCount > minCount)) return 1;
    let t = (count - minCount) / (maxCount - minCount);
    if (!Number.isFinite(t)) t = 1;
    t = Math.max(0, Math.min(1, t));
    return Math.pow(t, 0.65);
  };

  const loadToColor = (count, minCount, maxCount) => {
    const t = trafficNorm(count, minCount, maxCount);
    const gb = Math.round(255 - 255 * t);
    const h = gb.toString(16).padStart(2, "0");
    return `#ff${h}${h}`;
  };

  const loadToWeight = (count, minCount, maxCount) => {
    const t = trafficNorm(count, minCount, maxCount);
    return 0.9 + t * 6.5;
  };

  const selectedCounts = selectedBannerId
    ? data?.banner_traffic?.edge_counts?.[selectedBannerId] || null
    : null;

  const selectedStats = selectedBannerId
    ? data?.banner_traffic?.edge_stats?.[selectedBannerId] || { min: 1, max: 1 }
    : null;

  const statusMessage = (() => {
    if (!trafficMapPath || mapLoadStatus === 'no_path') return 'Сначала загрузите инвентарь.';
    if (mapLoadStatus === 'pending') return 'Карта трафика еще генерируется...';
    if (mapLoadStatus === 'error') return 'Не удалось загрузить карту трафика.';
    if (!data) return 'Загрузка данных...';
    return null;
  })();

  if (statusMessage) {
    return (
      <div
        className="traffic-map-status"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '240px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '16px',
          boxSizing: 'border-box',
          color: '#5b5d6b',
          fontFamily: "'Golos Text', 'Golos', system-ui, sans-serif",
          fontSize: '14px',
          lineHeight: '20px',
          background: '#f7f8fa',
        }}
      >
        {statusMessage}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div className="map-filters map-filters-on-map">
        <button
          type="button"
          onClick={() => {
            const mapCard = document.querySelector('.map-card') || document.querySelector('.om-map-wrap');
            if (!mapCard) return;

            if (document.fullscreenElement) {
              document.exitFullscreen().then(() => {
                setIsMapFullscreen(false);
              }).catch(err => console.error('Exit fullscreen error:', err));
            } else if (mapCard.requestFullscreen) {
              mapCard.requestFullscreen().then(() => {
                setIsMapFullscreen(true);
              }).catch(err => console.error('Enter fullscreen error:', err));
            }
          }}
          title={isMapFullscreen ? "Выйти из полного экрана" : "На весь экран"}
          style={{ marginLeft: 'auto' }}
        >
          ⛶
        </button>
      </div>

      <MapContainer
        center={
          bnr_zoom_coordinates && bnr_zoom_coordinates.lat != null
            ? [bnr_zoom_coordinates.lat, bnr_zoom_coordinates.lng]
            : Array.isArray(bnr_zoom_coordinates) && bnr_zoom_coordinates.length === 2
              ? bnr_zoom_coordinates
              : [47.2248606, 39.7022858]
        }
        zoom={zoomLevel || 12}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        zoomControl={false}
      >
        <InvalidateSizeOnShow
          active={isVisible}
          center={bnr_zoom_coordinates}
          zoom={zoomLevel}
        />
        {mapRef ? <MapBridge mapRef={mapRef} /> : null}
        <TileLayer
          url={CARTO_LIGHT_TILE_URL}
          attribution=""
        />

        {data.traffic.polylines.map((pl) => {
          const minCount = data.traffic.min_count;
          const maxCount = data.traffic.max_count;

          if (!selectedCounts) {
            return (
              <Polyline
                key={pl.edge_key}
                positions={pl.coords}
                pathOptions={{
                  color: loadToColor(pl.count, minCount, maxCount),
                  weight: loadToWeight(pl.count, minCount, maxCount),
                  opacity: 0.85,
                }}
              />
            );
          }

          const c = selectedCounts[pl.edge_key] || 0;
          if (!c) {
            return (
              <Polyline
                key={pl.edge_key}
                positions={pl.coords}
                pathOptions={{ color: "#d8d8d8", weight: 1.0, opacity: 0.12 }}
              />
            );
          }

          return (
            <Polyline
              key={pl.edge_key}
              positions={pl.coords}
              pathOptions={{
                color: loadToColor(c, selectedStats.min, selectedStats.max),
                weight: loadToWeight(c, selectedStats.min, selectedStats.max),
                opacity: 0.9,
              }}
            />
          );

        })}

         {data.banners.map((b) => {
          const isSelected = String(b.id) === String(selectedBannerId);
          const select_and_unSelect = isSelected ? '' : b.id;

          const bannerPopup = (
            <Popup closeButton={false} className="custom-map-popup">
              <BannerPopup
                id={b.id}
                address={b.address || b['Адрес'] || ''}
                photoLink={b.photo}
                mapLink={b.map}
                price={b.price ? `${b.price} ₽` : undefined}
                trafficActive={isSelected}
                onShowTraffic={() => setSelectedBannerId(select_and_unSelect)}
              />
            </Popup>
          );

          return (
            <Marker
              key={b.id}
              position={[b.lat, b.lon]}
              icon={isSelected ? selectedIcon : dotIcon}
              zIndexOffset={isSelected ? 1000 : 0}
            >
              {bannerPopup}
            </Marker>
          );
        })} 
      </MapContainer>
    </div>
  );
}
